import {
  formatPlayTestBooleanValue,
  getNonPlayerValue,
  isPlayTestOutcomeValueClear,
  normalizePlayTestBooleanValue,
  parsePlayTestEnumTypes,
} from "@seabrookstudios/pitch2table-core";

export const UNKNOWN_HISTOGRAM_LABEL = formatPlayTestBooleanValue(undefined);

/**
 * Values for a per-game tracking variable from play tests included in statistics.
 *
 * @param {Array<{ ignore?: boolean, nonPlayerOutcomes?: import("@seabrookstudios/pitch2table-core").PlayTestOutcomes[] }>} playTests
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId} metaId
 * @returns {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue[]}
 */
export const getIncludedPerGameValues = (playTests, metaId) => {
  return playTests
    .filter((pt) => pt.ignore)
    .map((pt) => getNonPlayerValue(pt.nonPlayerOutcomes || [], metaId));
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue} value
 * @returns {string}
 */
export const classifyBooleanHistogramValue = (value) => {
  return formatPlayTestBooleanValue(normalizePlayTestBooleanValue(value));
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue} value
 * @param {string[]} options
 * @returns {{ kind: "choice", label: string } | { kind: "unknown" } | { kind: "other", label: string }}
 */
export const classifyEnumHistogramValue = (value, options) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    isPlayTestOutcomeValueClear(value) ||
    typeof value !== "string"
  ) {
    return { kind: "unknown" };
  }

  const match = options.find((option) => option.toLocaleLowerCase() === value.toLocaleLowerCase());
  if (match) {
    return { kind: "choice", label: match };
  }

  return { kind: "other", label: value };
};

/**
 * Yes / No / Unknown counts. Unknown is always included, even at zero.
 *
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue[]} values
 * @returns {{ label: string, count: number }[]}
 */
export const buildBooleanHistogram = (values) => {
  const labels = [
    formatPlayTestBooleanValue(true),
    formatPlayTestBooleanValue(false),
    UNKNOWN_HISTOGRAM_LABEL,
  ];
  /** @type {Record<string, number>} */
  const counts = Object.fromEntries(labels.map((label) => [label, 0]));

  values.forEach((value) => {
    counts[classifyBooleanHistogramValue(value)] += 1;
  });

  return labels.map((label) => ({ label, count: counts[label] }));
};

/**
 * Counts per defined choice (including zeros), then unmatched recorded values, then Unknown.
 *
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue[]} values
 * @param {string|undefined} enumTypes
 * @returns {{ label: string, count: number }[]}
 */
export const buildEnumHistogram = (values, enumTypes) => {
  const options = parsePlayTestEnumTypes(enumTypes);
  const choiceCounts = new Map(options.map((option) => [option, 0]));
  /** @type {Map<string, number>} */
  const otherCounts = new Map();
  let unknown = 0;

  values.forEach((value) => {
    const classified = classifyEnumHistogramValue(value, options);
    if (classified.kind === "unknown") {
      unknown += 1;
      return;
    }
    if (classified.kind === "choice") {
      choiceCounts.set(classified.label, (choiceCounts.get(classified.label) || 0) + 1);
      return;
    }
    otherCounts.set(classified.label, (otherCounts.get(classified.label) || 0) + 1);
  });

  return [
    ...options.map((label) => ({ label, count: choiceCounts.get(label) || 0 })),
    ...Array.from(otherCounts.entries()).map(([label, count]) => ({ label, count })),
    { label: UNKNOWN_HISTOGRAM_LABEL, count: unknown },
  ];
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue[]} values
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataValueType|undefined} valueType
 * @param {string|undefined} [enumTypes]
 * @returns {{ label: string, count: number }[]}
 */
export const buildPerGameCategoricalHistogram = (values, valueType, enumTypes) => {
  if (valueType === "ENUM") {
    return buildEnumHistogram(values, enumTypes);
  }

  return buildBooleanHistogram(values);
};
